import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Skeleton, Avatar, Select, MenuItem,
    FormControl, InputLabel, IconButton, Modal, Tooltip
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from 'primereact/toast';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import * as XLSX from 'xlsx';
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

function StatCard({ label, value, hint, accent }) {
    return (
        <Box sx={{
            flex: 1, minWidth: '150px', backgroundColor: 'background.light',
            borderRadius: '12px', padding: '13px 16px',
            boxShadow: '0px 0px 25px 0px #00000012',
            borderLeft: '3px solid', borderColor: accent
        }}>
            <Typography sx={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.07em', color: 'text.secondary' }}>{label}</Typography>
            <Typography sx={{ fontSize: '22px', fontWeight: 600, lineHeight: 1.25 }}>{value}</Typography>
            <Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>{hint}</Typography>
        </Box>
    );
}

function initialsOf(name) {
    if (!name) return '--';
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

const NUM = { textAlign: 'right', backgroundColor: 'white', fontVariantNumeric: 'tabular-nums', fontSize: '12px' };

// Salary block: tinted and rule-separated so money reads as its own group
// rather than as three more attendance counts.
const PAY = { ...NUM, backgroundColor: '#FFF8FB', borderLeft: '2px solid #F8A5C9' };

// Money is fetched from a different endpoint, not filtered out of a shared one:
// the payroll route is HR-gated server-side and the attendance route never
// carries salary at all, so a non-HR session has nothing to find in the payload.
const HR_ROLES = ['super_admin', 'hr_admin'];

export function StaffMonthlyReport() {
    const selectedHub = useSelector(state => state.user.selected_hub);
    const toast = useRef(null);
    const userRole = localStorage.getItem('role');
    const isHR = HR_ROLES.includes(userRole);
    const isSuperAdmin = userRole === 'super_admin';

    const [month, setMonth] = useState(dayjs().subtract(1, 'month').format('YYYY-MM'));
    const [departments, setDepartments] = useState([]);
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [detailOpen, setDetailOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const hubId = useMemo(() => {
        const normalize = (value) => {
            if (value === null || value === undefined) return null;
            if (Array.isArray(value)) return normalize(value[0]);
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        };
        const fromStore = normalize(selectedHub);
        if (fromStore) return fromStore;
        const stored = localStorage.getItem('selected_hub');
        if (!stored) return null;
        try {
            return normalize(JSON.parse(stored));
        } catch (e) {
            return normalize(stored);
        }
    }, [selectedHub]);

    const showToast = (severity, summary, detailText) => {
        if (toast.current) toast.current.show({ severity, summary, detail: detailText, life: 4000 });
    };

    useEffect(() => {
        if (!hubId) return;
        fetchReport();
        fetchDepartments();
    }, [hubId, month, departmentFilter]);

    const fetchDepartments = async () => {
        try {
            const res = await CustomAxios.get('staff/departments/', { params: { hub_id: hubId, is_active: true } });
            setDepartments(res.data || []);
        } catch (error) {
            setDepartments([]);
        }
    };

    const fetchReport = async () => {
        try {
            setLoading(true);
            const params = { hub_id: hubId, month };
            if (departmentFilter) params.department_id = departmentFilter;
            const res = await CustomAxios.get(
                isHR ? 'staff/payroll/report/' : 'staff/reports/monthly/',
                { params },
            );
            setData(res.data);
        } catch (error) {
            showToast('error', 'Error', error?.response?.data?.error || 'Failed to load report');
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const openDetail = async (row) => {
        setDetailOpen(true);
        setDetail(null);
        try {
            setDetailLoading(true);
            const res = await CustomAxios.get(`staff/reports/staff/${row.staff_id}/`, { params: { month } });
            setDetail({ ...res.data, row });
        } catch (error) {
            showToast('error', 'Error', 'Could not load the records behind this row');
            setDetailOpen(false);
        } finally {
            setDetailLoading(false);
        }
    };

    // Excel is built client-side with the xlsx package the orders screen already
    // uses, so the sheets look like siblings.
    const exportExcel = () => {
        if (!data?.rows?.length) {
            showToast('warn', 'Nothing to export', 'This month has no staff records');
            return;
        }
        const sheetRows = data.rows.map(r => ({
            'Employee ID': r.employee_id,
            'Name': r.full_name,
            'Department': r.department_name || '',
            'Designation': r.designation || '',
            'Employed from': r.employed_from,
            'Employed to': r.employed_to,
            'Working days': r.working_days,
            'Present': r.present_days,
            'Half days': r.half_days,
            'Absent': r.absent_days,
            'Missing punch': r.missing_punch_days,
            'Casual': r.leave_casual,
            'Sick': r.leave_sick,
            'Earned': r.leave_earned,
            'LOP': r.leave_lop,
            'Comp-off earned': r.comp_off_earned,
            'Comp-off used': r.comp_off_used,
            'On duty': r.on_duty_days,
            'Permission (h)': r.permission_hours,
            'Late marks': r.late_marks,
            'Late penalty (d)': r.late_penalty_days,
            'Payable days': r.payable_days,
            ...(isHR ? {
                'Gross': r.salary_configured ? r.monthly_gross : '',
                'Per-day rate': r.salary_configured ? r.per_day_rate : '',
                'Base pay': r.salary_configured ? r.base_pay : '',
                'Unpaid days': r.salary_configured ? r.unpaid_days : '',
                'Deduction': r.salary_configured ? r.deduction : '',
                'Net salary': r.salary_configured ? r.net_salary : 'NOT SET',
            } : {}),
        }));

        const worksheet = XLSX.utils.json_to_sheet(sheetRows, { origin: 'A5' });
        XLSX.utils.sheet_add_aoa(worksheet, [
            ['Consolidated Monthly Attendance'],
            [`${data.hub_name || 'Hub'} · ${data.month_label}`],
            [`${data.summary.staff_count} staff · ${data.summary.working_days} working days · ${data.summary.avg_attendance}% average attendance`],
            [],
        ], { origin: 'A1' });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance');
        XLSX.writeFile(workbook, `attendance_${data.hub_name || 'hub'}_${month}.xlsx`.replace(/\s+/g, '_'));
    };

    // PDF is generated server-side by reportlab — no frontend PDF dependency.
    const exportPDF = async () => {
        try {
            setExporting(true);
            const params = { hub_id: hubId, month };
            if (departmentFilter) params.department_id = departmentFilter;
            const res = await CustomAxios.get('staff/reports/monthly/export/', {
                params, responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `attendance_${data?.hub_name || 'hub'}_${month}.pdf`.replace(/\s+/g, '_');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            showToast('error', 'Error', 'PDF export failed');
        } finally {
            setExporting(false);
        }
    };

    const shiftMonth = (delta) => setMonth(dayjs(`${month}-01`).add(delta, 'month').format('YYYY-MM'));

    const headers = [
        'Staff', 'Working\ndays', 'Present', 'Half', 'Absent',
        'CL', 'SL', 'EL', 'LOP', 'Comp\nearned', 'Comp\nused',
        'OD', 'Perm (h)', 'Late\nmarks', 'Payable\ndays',
        ...(isHR ? ['Gross\n₹', 'Deduction\n₹', 'Net salary\n₹'] : []),
    ];

    const lockMonth = async () => {
        if (!window.confirm(
            `Mark ${data?.month_label} as disbursed?\n\n` +
            `Attendance edits and request approvals for that month will be refused ` +
            `from then on, so these figures can no longer change.`
        )) return;
        try {
            setExporting(true);
            const res = await CustomAxios.post('staff/payroll/locks/', {
                hub_id: hubId, month,
            });
            showToast('success', 'Locked', res.data.message);
            fetchReport();
        } catch (error) {
            showToast('error', 'Error', error?.response?.data?.error || 'Could not lock the month');
        } finally {
            setExporting(false);
        }
    };

    const unlockMonth = async () => {
        if (!window.confirm(
            `Reopen ${data?.month_label}?\n\n` +
            `Already-disbursed figures will be able to move again.`
        )) return;
        try {
            setExporting(true);
            const res = await CustomAxios.delete(`staff/payroll/locks/${data.lock_id}/`);
            showToast('success', 'Reopened', res.data.message);
            fetchReport();
        } catch (error) {
            showToast('error', 'Error', error?.response?.data?.error || 'Could not reopen the month');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Typography variant="h5">Monthly Report</Typography>
                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        border: '1px solid', borderColor: 'secondary.border',
                        borderRadius: '6px', padding: '2px 6px'
                    }}>
                        <IconButton size="small" onClick={() => shiftMonth(-1)}><ChevronLeftIcon fontSize="small" /></IconButton>
                        <Typography sx={{ fontSize: '13px', minWidth: '110px', textAlign: 'center' }}>
                            {dayjs(`${month}-01`).format('MMMM YYYY')}
                        </Typography>
                        <IconButton size="small" onClick={() => shiftMonth(1)}
                            disabled={dayjs(`${month}-01`).isAfter(dayjs().subtract(1, 'month'), 'month')}>
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: '160px' }}>
                        <InputLabel>Department</InputLabel>
                        <Select value={departmentFilter} label="Department" onChange={(e) => setDepartmentFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={exportExcel}>Export Excel</Button>
                    <Button variant="outlined" onClick={exportPDF} disabled={exporting}>
                        {exporting ? 'Generating…' : 'Export PDF'}
                    </Button>
                    {isHR && data && !data.is_locked && (
                        <Button variant="contained" onClick={lockMonth} disabled={exporting}>
                            Lock &amp; mark disbursed
                        </Button>
                    )}
                </Box>
            </Box>

            {data?.is_locked && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
                    padding: '10px 16px', backgroundColor: '#FFF7E6',
                    border: '1px solid #FFD591', borderRadius: '8px'
                }}>
                    <LockOutlinedIcon sx={{ fontSize: 18, color: '#A85700' }} />
                    <Typography sx={{ fontSize: '12.5px', color: '#A85700', flex: 1 }}>
                        <b>{data.month_label} is locked.</b> Attendance and approvals for this month are
                        frozen, so these figures can no longer change.
                        {data.locked_by_name ? ` Marked disbursed by ${data.locked_by_name}` : ''}
                        {data.locked_on ? ` on ${dayjs(data.locked_on).format('DD MMM YYYY')}.` : ''}
                    </Typography>
                    {isSuperAdmin && data.lock_id && (
                        <Button size="small" variant="outlined" onClick={unlockMonth} disabled={exporting}>
                            Reopen month
                        </Button>
                    )}
                </Box>
            )}

            {data?.summary?.unrecorded_days > 0 && (
                <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2,
                    padding: '10px 16px', backgroundColor: '#FFF1F0',
                    border: '1px solid #FFCCC7', borderRadius: '8px'
                }}>
                    <Typography sx={{ fontSize: '15px', color: '#F5222D', lineHeight: 1.2 }}>!</Typography>
                    <Typography sx={{ fontSize: '12.5px', color: '#F5222D' }}>
                        <b>{data.summary.unrecorded_days} working day(s) have nothing recorded</b> — no
                        attendance entry and no approved request. They are currently being counted as
                        payable. Run <code style={{ fontFamily: 'monospace' }}>close_attendance_day</code> for
                        this month, or fill them in on the Daily Attendance screen, before treating these
                        figures as final.
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', mb: 3 }}>
                <StatCard label="Working days" value={data?.summary?.working_days ?? '—'} hint="excl. week-offs & holidays" accent="primary.main" />
                <StatCard label="Avg attendance" value={data ? `${data.summary.avg_attendance}%` : '—'} hint={`${data?.summary?.staff_count ?? '—'} staff`} accent="borders.green" />
                <StatCard label="Total late marks" value={data?.summary?.total_late_marks ?? '—'} hint="beyond grace" accent="#FA8C16" />
                <StatCard label="LOP days" value={data?.summary?.total_lop_days ?? '—'} hint="unpaid across hub" accent="borders.red" />
                <StatCard label="Comp-offs earned" value={data?.summary?.comp_offs_earned ?? '—'} hint={`${data?.summary?.comp_offs_used ?? 0} used`} accent="button.blue" />
            </Box>

            {isHR && data?.summary?.total_net !== undefined && (
                <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', mb: 3 }}>
                    <StatCard
                        label="Total base pay"
                        value={`₹${Number(data.summary.total_gross).toLocaleString('en-IN')}`}
                        hint="pro-rated for partial months" accent="primary.main"
                    />
                    <StatCard
                        label="Total deductions"
                        value={`₹${Number(data.summary.total_deduction).toLocaleString('en-IN')}`}
                        hint="unpaid days × per-day rate" accent="borders.red"
                    />
                    <StatCard
                        label="Total net payable"
                        value={`₹${Number(data.summary.total_net).toLocaleString('en-IN')}`}
                        hint="sum of rounded payslips" accent="borders.green"
                    />
                    <StatCard
                        label="Without salary set"
                        value={data.summary.staff_without_salary}
                        hint="excluded from totals" accent="#FA8C16"
                    />
                </Box>
            )}

            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012' }}>
                <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                        {data?.month_label || dayjs(`${month}-01`).format('MMMM YYYY')}
                        {data?.hub_name ? ` · ${data.hub_name}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        read-only · computed at request time · click a row for the records behind it
                    </Typography>
                </Box>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {headers.map((h, i) => (
                                <TableCell key={i} sx={{
                                    fontWeight: 600, backgroundColor: 'background.table',
                                    whiteSpace: 'pre-line', fontSize: '11px',
                                    textAlign: i === 0 ? 'left' : 'right',
                                }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={headers.length} sx={{ backgroundColor: 'white', height: '180px' }}>
                                {[0, 1, 2].map(i => <Skeleton key={i} animation="wave" sx={{ height: '44px', background: 'rgb(0 0 0 / 6%)' }} />)}
                            </TableCell></TableRow>
                        ) : data?.rows?.length ? data.rows.map(row => (
                            <TableRow
                                key={row.staff_id}
                                hover
                                onClick={() => openDetail(row)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Avatar sx={{ width: 26, height: 26, fontSize: '10px', bgcolor: 'background.table', color: 'button.blue' }}>
                                            {initialsOf(row.full_name)}
                                        </Avatar>
                                        <Box>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                                                {row.full_name}
                                                {row.is_partial_month && (
                                                    <Tooltip title={`Employed ${row.employed_from} → ${row.employed_to}`} arrow>
                                                        <Typography component="span" sx={{ ml: 1, fontSize: '9px', color: '#FA8C16', textTransform: 'uppercase' }}>
                                                            partial
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </Typography>
                                            <Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>
                                                {row.employee_id}{row.designation ? ` · ${row.designation}` : ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={NUM}>{row.working_days}</TableCell>
                                <TableCell sx={NUM}>{row.present_days}</TableCell>
                                <TableCell sx={NUM}>{row.half_days}</TableCell>
                                <TableCell sx={{ ...NUM, color: row.absent_days > 0 ? '#F5222D' : 'inherit' }}>{row.absent_days}</TableCell>
                                <TableCell sx={NUM}>{row.leave_casual}</TableCell>
                                <TableCell sx={NUM}>{row.leave_sick}</TableCell>
                                <TableCell sx={NUM}>{row.leave_earned}</TableCell>
                                <TableCell sx={{ ...NUM, color: Number(row.leave_lop) > 0 ? '#F5222D' : 'inherit' }}>{row.leave_lop}</TableCell>
                                <TableCell sx={NUM}>{row.comp_off_earned}</TableCell>
                                <TableCell sx={NUM}>{row.comp_off_used}</TableCell>
                                <TableCell sx={NUM}>{row.on_duty_days}</TableCell>
                                <TableCell sx={NUM}>{row.permission_hours}</TableCell>
                                <TableCell sx={{ ...NUM, color: Number(row.late_penalty_days) > 0 ? '#FA8C16' : 'inherit' }}>
                                    {row.late_marks}
                                </TableCell>
                                <TableCell sx={{ ...NUM, fontWeight: 600 }}>{row.payable_days}</TableCell>
                                {isHR && (row.salary_configured ? (
                                    <>
                                        <TableCell sx={PAY}>{Number(row.monthly_gross).toLocaleString('en-IN')}</TableCell>
                                        <TableCell sx={{ ...PAY, color: Number(row.deduction) > 0 ? '#F5222D' : 'inherit' }}>
                                            {Number(row.deduction).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </TableCell>
                                        <TableCell sx={{ ...PAY, fontWeight: 600 }}>
                                            {Number(row.net_salary).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </TableCell>
                                    </>
                                ) : (
                                    // Never a zero. A zero reads as a computed answer;
                                    // this is a missing input.
                                    <TableCell sx={{ ...PAY, textAlign: 'left' }} colSpan={3}>
                                        <Tooltip title={row.payroll_note || 'No salary configured'} arrow>
                                            <Box sx={{
                                                display: 'inline-flex', backgroundColor: '#FFF7E6',
                                                border: '1px solid #FA8C16', borderRadius: '2px', padding: '1px 8px'
                                            }}>
                                                <Typography sx={{ fontSize: '10px', lineHeight: '18px', color: '#FA8C16', textTransform: 'uppercase' }}>
                                                    No salary set
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                ))}
                            </TableRow>
                        )) : (
                            <TableRow><TableCell colSpan={headers.length} sx={{ textAlign: 'center', backgroundColor: 'white', height: '180px', fontWeight: 700 }}>
                                No staff employed at this hub during {dayjs(`${month}-01`).format('MMMM YYYY')}
                            </TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{
                mt: 3, backgroundColor: 'background.light', borderRadius: '12px',
                boxShadow: '0px 0px 25px 0px #00000012', overflow: 'hidden', maxWidth: '760px'
            }}>
                <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>How payable days is arrived at</Typography>
                </Box>
                <Box sx={{ p: '15px 18px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                    {[
                        ['Start with', 'Working days (calendar days − week-offs − holidays)'],
                        ['Bounded by', 'Date of joining and last working day'],
                        ['Subtract', 'Absences and unpaid (LOP) leave'],
                        ['Subtract', 'Half-day shortfalls, 0.5 each'],
                        ['Subtract', 'Late-mark penalty, every 3rd late mark = 0.5 day'],
                        ['Subtract', 'Unregularized missing punches'],
                        ['Never', 'Below zero'],
                    ].map(([label, value], i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary', minWidth: '90px' }}>{label}</Typography>
                            <Typography sx={{ fontSize: '12px', textAlign: 'right' }}>{value}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Modal open={detailOpen} onClose={() => setDetailOpen(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '94%', md: 760 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 3,
                    borderRadius: 2, maxHeight: '88vh', overflow: 'auto'
                }}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                        {detail?.row?.full_name || 'Records'}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 2 }}>
                        {detail?.row?.employee_id} · {dayjs(`${month}-01`).format('MMMM YYYY')}
                    </Typography>

                    {detailLoading ? (
                        [0, 1, 2].map(i => <Skeleton key={i} animation="wave" sx={{ height: '40px' }} />)
                    ) : detail ? (
                        <>
                            {isHR && detail.row?.salary_configured && (
                                <Box sx={{
                                    mb: 3, border: '1px solid #F8A5C9', borderRadius: '8px',
                                    backgroundColor: '#FFF8FB', overflow: 'hidden'
                                }}>
                                    <Box sx={{ padding: '10px 16px', borderBottom: '1px solid #F8A5C9' }}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Salary breakdown</Typography>
                                    </Box>
                                    <Box sx={{ p: '12px 16px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                                        {[
                                            ['Monthly gross', `₹${Number(detail.row.monthly_gross).toLocaleString('en-IN')} · effective ${dayjs(detail.row.salary_effective_from).format('DD MMM YYYY')}`],
                                            ['Employed', `${detail.row.employed_calendar_days} of ${detail.row.calendar_days_in_month} calendar days`],
                                            ['Base pay', `₹${Number(detail.row.base_pay).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
                                            ['Per-day rate', `₹${detail.row.per_day_rate} · gross ÷ ${detail.row.rate_divisor} (${detail.row.rate_basis.replace('_', ' ')})`],
                                            ['Unpaid days', `${detail.row.unpaid_days} of ${detail.row.working_days} working days`],
                                            ['Deduction', `₹${Number(detail.row.deduction).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
                                            ['Net salary', `₹${Number(detail.row.net_salary).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
                                        ].map(([label, value], i, arr) => (
                                            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{label}</Typography>
                                                <Typography sx={{
                                                    fontSize: '12px', textAlign: 'right',
                                                    fontWeight: i === arr.length - 1 ? 700 : 500,
                                                }}>
                                                    {value}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {detail.row.payroll_note && (
                                            <Typography sx={{ fontSize: '11px', color: '#A85700', mt: 0.5 }}>
                                                {detail.row.payroll_note}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1 }}>Attendance</Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: '300px' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {['Date', 'In', 'Out', 'Worked', 'Status', 'Late', 'Source', 'Remarks'].map((h, i) => (
                                                <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', fontSize: '11px' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.attendance.length ? detail.attendance.map(a => (
                                            <TableRow key={a.date}>
                                                <TableCell sx={{ fontSize: '11.5px', whiteSpace: 'nowrap' }}>{dayjs(a.date).format('DD MMM')}</TableCell>
                                                <TableCell sx={{ fontSize: '11.5px' }}>{a.arrival_time || '—'}</TableCell>
                                                <TableCell sx={{ fontSize: '11.5px' }}>{a.dispersal_time || '—'}</TableCell>
                                                <TableCell sx={{ fontSize: '11.5px' }}>
                                                    {a.worked_minutes != null ? `${Math.floor(a.worked_minutes / 60)}h ${String(a.worked_minutes % 60).padStart(2, '0')}m` : '—'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '11.5px' }}>{a.status_label}</TableCell>
                                                <TableCell sx={{ fontSize: '11.5px' }}>{a.late_by_minutes ? `${a.late_by_minutes}m` : '—'}</TableCell>
                                                <TableCell sx={{ fontSize: '11px', textTransform: 'uppercase', color: 'text.secondary' }}>{a.source}</TableCell>
                                                <TableCell sx={{ fontSize: '11.5px' }}>{a.remarks || '—'}</TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 3, fontSize: '12px' }}>
                                                No attendance records for this month
                                            </TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1 }}>Approved requests</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Ref', 'Type', 'Period', 'Qty', 'Paid', 'Reason'].map((h, i) => (
                                                <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', fontSize: '11px' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.requests.length ? detail.requests.map(r => (
                                            <TableRow key={r.id}>
                                                <TableCell sx={{ fontSize: '11px', fontFamily: 'monospace' }}>REQ-{r.id}</TableCell>
                                                <TableCell sx={{ fontSize: '11.5px' }}>
                                                    {r.request_type_label}{r.leave_category ? ` · ${r.leave_category}` : ''}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '11.5px', whiteSpace: 'nowrap' }}>
                                                    {dayjs(r.from_date).format('DD MMM')}
                                                    {r.to_date !== r.from_date ? ` → ${dayjs(r.to_date).format('DD MMM')}` : ''}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '11.5px' }}>
                                                    {r.request_type === 'permission' ? `${r.hours_count} h` : `${r.days_count} d`}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '11.5px', color: r.is_paid ? '#52C41A' : '#F5222D' }}>
                                                    {r.is_paid ? 'Paid' : 'LOP'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '11.5px' }}>{r.reason || '—'}</TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, fontSize: '12px' }}>
                                                No approved requests this month
                                            </TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    ) : null}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button onClick={() => setDetailOpen(false)}>Close</Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
